import { useEffect, useState } from 'react';
import { adminApi } from '../../services/api';
import type { PlatformMetrics } from '../../types/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  UsersIcon,
  ChatBubbleLeftRightIcon,
  BoltIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

export default function MetricsPage() {
  const [metrics, setMetrics] = useState<PlatformMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    setIsLoading(true);
    try {
      const data = await adminApi.getMetrics();
      setMetrics(data);
    } catch (error) {
      console.error('Failed to load metrics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Platform Metrics</h1>
        <p className="text-gray-600 mt-1">Overview of platform usage and statistics</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          icon={UsersIcon}
          label="Total Users"
          value={metrics?.totalUsers || 0}
          color="blue"
        />
        <MetricCard
          icon={UsersIcon}
          label="Active Users"
          value={metrics?.activeUsers || 0}
          color="green"
        />
        <MetricCard
          icon={ChatBubbleLeftRightIcon}
          label="Total Sessions"
          value={metrics?.totalSessions || 0}
          color="purple"
        />
        <MetricCard
          icon={ChatBubbleLeftRightIcon}
          label="Total Messages"
          value={metrics?.totalMessages || 0}
          color="orange"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <BoltIcon className="h-6 w-6 text-wise" />
            <h2 className="text-lg font-semibold text-gray-900">Messages This Month</h2>
          </div>
          <div className="text-4xl font-bold text-wise">
            {metrics?.messagesThisMonth?.toLocaleString() || 0}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <ChartBarIcon className="h-6 w-6 text-wise" />
            <h2 className="text-lg font-semibold text-gray-900">Activity Overview</h2>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Active User Rate</span>
              <span className="font-semibold text-gray-900">
                {metrics && metrics.totalUsers > 0
                  ? Math.round((metrics.activeUsers / metrics.totalUsers) * 100)
                  : 0}%
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full">
              <div
                className="h-full bg-green-500 rounded-full"
                style={{
                  width: `${
                    metrics && metrics.totalUsers > 0
                      ? (metrics.activeUsers / metrics.totalUsers) * 100
                      : 0
                  }%`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface MetricCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  color: 'blue' | 'green' | 'purple' | 'orange';
}

function MetricCard({ icon: Icon, label, value, color }: MetricCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <p className="text-sm text-gray-600">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
}
